import React from 'react'
import { useAuth } from '../lib/auth.jsx'
import { api } from '../lib/api.js'

function Section({ title, subtitle, actions, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-lg font-semibold">{title}</div>
          {subtitle ? <div className="text-sm text-white/60">{subtitle}</div> : null}
        </div>
        <div className="flex flex-wrap gap-2">{actions}</div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  )
}

function JsonEditor({ value, onChange, height = 420 }) {
  const [raw, setRaw] = React.useState(typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2))
  const [bad, setBad] = React.useState(false)

  React.useEffect(() => {
    const next = typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)
    setRaw(next)
    setBad(typeof value === 'string')
  }, [value])

  return (
    <div>
      <textarea
        className="w-full rounded-xl border border-white/10 bg-black/30 p-3 font-mono text-xs text-white/80 outline-none focus:border-white/20"
        style={{ minHeight: height }}
        value={raw}
        onChange={(e) => {
          const v = e.target.value
          setRaw(v)
          try {
            const parsed = JSON.parse(v)
            setBad(false)
            onChange(parsed)
          } catch {
            setBad(true)
            onChange(v)
          }
        }}
      />
      {bad ? <div className="mt-2 text-sm text-red-300">Invalid JSON. Fix formatting before saving.</div> : null}
    </div>
  )
}

export default function Admin() {
  const { user, token, loading } = useAuth()

  const [tab, setTab] = React.useState('site')
  const [ok, setOk] = React.useState('')
  const [err, setErr] = React.useState('')

  const [site, setSite] = React.useState(null)
  const [news, setNews] = React.useState(null)
  const [milestones, setMilestones] = React.useState(null)
  const [roadmap, setRoadmap] = React.useState(null)
  const [media, setMedia] = React.useState(null)
  const [discordCfg, setDiscordCfg] = React.useState(null)

  const [launcherCfg, setLauncherCfg] = React.useState(null)
  const [launcherChannels, setLauncherChannels] = React.useState([])
  const [launcherMeta, setLauncherMeta] = React.useState(null)
  const [uploadChannel, setUploadChannel] = React.useState('live')

  const [admins, setAdmins] = React.useState([])
  const [adminEmail, setAdminEmail] = React.useState('')

  const [betaKeys, setBetaKeys] = React.useState([])
  const [genChannel, setGenChannel] = React.useState('beta')
  const [genCount, setGenCount] = React.useState(10)
  const [genMaxUses, setGenMaxUses] = React.useState(1)
  const [genExpiresAt, setGenExpiresAt] = React.useState('')
  const [generated, setGenerated] = React.useState([])

  const isAdmin = user?.role === 'admin'

  const tabs = [
    { id: 'site', label: 'Site' },
    { id: 'news', label: 'News' },
    { id: 'milestones', label: 'Milestones' },
    { id: 'roadmap', label: 'Roadmap' },
    { id: 'media', label: 'Media' },
    { id: 'launcher', label: 'Launcher' },
    { id: 'beta', label: 'Beta Keys' },
    { id: 'discord', label: 'Discord' },
    { id: 'admins', label: 'Admins' }
  ]

  async function loadAll() {
    setErr('')
    setOk('')
    setGenerated([])
    try {
      const [siteResp, newsResp, msResp, rmResp, mediaResp, discResp, cfgResp, chResp, adminResp, betaResp] =
        await Promise.all([
          api('/api/admin/site', { token }),
          api('/api/admin/news', { token }),
          api('/api/admin/milestones', { token }),
          api('/api/admin/roadmap', { token }),
          api('/api/admin/media', { token }),
          api('/api/admin/discord', { token }),
          api('/api/admin/launcher/config', { token }),
          api('/api/launcher/channels', { token }),
          api('/api/admin/admins', { token }),
          api('/api/admin/beta/keys', { token })
        ])

      setSite(siteResp.site ?? {})
      setNews({ items: newsResp.items ?? [] })
      setMilestones({ items: msResp.items ?? [] })
      setRoadmap({ items: rmResp.items ?? [] })
      setMedia({ media: mediaResp.media ?? {} })
      setDiscordCfg({ discord: discResp.discord ?? {} })

      setLauncherCfg(cfgResp.config ?? {})
      setLauncherChannels(chResp.channels ?? [])
      setAdmins(adminResp.items ?? [])
      setBetaKeys(betaResp.items ?? [])

      // meta for live by default
      const m = await api('/api/launcher/meta?channel=live')
      setLauncherMeta(m)
      setUploadChannel('live')
    } catch (e) {
      setErr(e.message || 'Failed to load admin data.')
    }
  }

  React.useEffect(() => {
    if (!token || !isAdmin) return
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin])

  async function save(which) {
    setErr('')
    setOk('')
    try {
      if (!token) throw new Error('Not logged in.')

      if (which === 'site') {
        await api('/api/admin/site', { method: 'PUT', token, body: site })
        setOk('Saved site settings.')
      } else if (which === 'news') {
        await api('/api/admin/news', { method: 'PUT', token, body: news })
        setOk('Saved news.')
      } else if (which === 'milestones') {
        await api('/api/admin/milestones', { method: 'PUT', token, body: milestones })
        setOk('Saved milestones.')
      } else if (which === 'roadmap') {
        await api('/api/admin/roadmap', { method: 'PUT', token, body: roadmap })
        setOk('Saved roadmap.')
      } else if (which === 'media') {
        await api('/api/admin/media', { method: 'PUT', token, body: media })
        setOk('Saved media.')
      } else if (which === 'discord') {
        await api('/api/admin/discord', { method: 'PUT', token, body: discordCfg })
        setOk('Saved Discord config.')
      } else if (which === 'launcher') {
        await api('/api/admin/launcher/config', { method: 'PUT', token, body: { config: launcherCfg } })
        setOk('Saved launcher config.')
      }
    } catch (e) {
      setErr(e.message || 'Save failed.')
    }
  }

  async function refreshMeta(ch) {
    try {
      const m = await api(`/api/launcher/meta?channel=${encodeURIComponent(ch || 'live')}`)
      setLauncherMeta(m)
    } catch (e) {
      setErr(e.message || 'Failed to load launcher meta.')
    }
  }

  async function uploadInstaller(file) {
    setErr('')
    setOk('')
    if (!file) return
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`/api/admin/launcher/upload?channel=${encodeURIComponent(uploadChannel)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      })
      if (!res.ok) {
        const txt = await res.text().catch(() => '')
        throw new Error(txt || `Upload failed (${res.status})`)
      }
      setOk(`Uploaded installer to channel: ${uploadChannel}`)
      await loadAll()
      await refreshMeta(uploadChannel)
    } catch (e) {
      setErr(e.message || 'Upload failed.')
    }
  }

  async function addAdmin() {
    setErr('')
    setOk('')
    try {
      await api('/api/admin/admins', { method: 'POST', token, body: { email: adminEmail } })
      setAdminEmail('')
      const r = await api('/api/admin/admins', { token })
      setAdmins(r.items ?? [])
      setOk('Admin added.')
    } catch (e) {
      setErr(e.message || 'Failed to add admin.')
    }
  }

  async function removeAdmin(email) {
    setErr('')
    setOk('')
    try {
      await api('/api/admin/admins', { method: 'DELETE', token, body: { email } })
      const r = await api('/api/admin/admins', { token })
      setAdmins(r.items ?? [])
      setOk('Admin removed.')
    } catch (e) {
      setErr(e.message || 'Failed to remove admin.')
    }
  }

  async function generateKeys() {
    setErr('')
    setOk('')
    setGenerated([])
    try {
      const body = {
        channel: genChannel,
        count: Number(genCount) || 1,
        maxUses: Number(genMaxUses) || 1,
        expiresAt: genExpiresAt ? new Date(genExpiresAt).toISOString() : null
      }
      const r = await api('/api/admin/beta/keys', { method: 'POST', token, body })
      setGenerated(r.codes || [])
      const list = await api('/api/admin/beta/keys', { token })
      setBetaKeys(list.items ?? [])
      setOk('Keys generated.')
    } catch (e) {
      setErr(e.message || 'Failed to generate keys.')
    }
  }

  async function revokeKey(code) {
    setErr('')
    setOk('')
    try {
      await api('/api/admin/beta/revoke', { method: 'POST', token, body: { code } })
      const list = await api('/api/admin/beta/keys', { token })
      setBetaKeys(list.items ?? [])
      setOk('Key revoked.')
    } catch (e) {
      setErr(e.message || 'Failed to revoke key.')
    }
  }

  if (loading) {
    return <div className="mx-auto max-w-6xl px-4 py-12 text-white/60">Loading…</div>
  }
  if (!user) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-12">
        <h1 className="text-3xl font-semibold">Admin</h1>
        <p className="mt-3 text-white/70">Login first.</p>
      </div>
    )
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-12">
        <h1 className="text-3xl font-semibold">Admin</h1>
        <p className="mt-3 text-white/70">
          Your account is not an admin. Add your email to <span className="text-white">ADMIN_EMAILS</span> in <span className="text-white">server/.env</span>,
          restart the server, then logout/login again.
        </p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Admin Dashboard</h1>
          <div className="mt-1 text-sm text-white/60">Logged in as <span className="text-white/80">{user.email}</span></div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} className="rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-sm text-white/70 hover:bg-white/5">Refresh</button>
        </div>
      </div>

      {(ok || err) ? (
        <div className={`mt-6 rounded-xl border px-4 py-3 text-sm ${err ? 'border-red-500/30 bg-red-500/10 text-red-200' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'}`}>
          {err || ok}
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'rounded-xl px-4 py-2 text-sm border ' +
              (tab === t.id ? 'border-white/25 bg-white/10 text-white' : 'border-white/10 bg-black/20 text-white/70 hover:border-white/20')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6">
        {tab === 'site' && (
          <Section title="Site Settings" subtitle="Branding, hero content, links, footer." actions={
            <button onClick={() => save('site')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={site} onChange={setSite} />
          </Section>
        )}

        {tab === 'news' && (
          <Section title="News" subtitle="Posts shown on /news." actions={
            <button onClick={() => save('news')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={news} onChange={setNews} />
          </Section>
        )}

        {tab === 'milestones' && (
          <Section title="Homepage Milestones" subtitle="The cinematic timeline on the homepage." actions={
            <button onClick={() => save('milestones')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={milestones} onChange={setMilestones} />
          </Section>
        )}

        {tab === 'roadmap' && (
          <Section title="Roadmap" subtitle="Public roadmap items (Planned / In Progress / Done)." actions={
            <button onClick={() => save('roadmap')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={roadmap} onChange={setRoadmap} />
          </Section>
        )}

        {tab === 'media' && (
          <Section title="Media" subtitle="Trailer video, screenshots carousel, and GIF clips." actions={
            <button onClick={() => save('media')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={media} onChange={setMedia} />
          </Section>
        )}

        {tab === 'launcher' && (
          <div className="grid gap-6">
            <Section title="Launcher Config" subtitle="Manage download channels and protection flags." actions={
              <button onClick={() => save('launcher')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
            }>
              <JsonEditor value={launcherCfg} onChange={setLauncherCfg} />
            </Section>

            <Section title="Upload Installer" subtitle="Upload an EXE per channel (live/beta/etc.).">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <label className="text-sm text-white/70">Channel</label>
                <select
                  value={uploadChannel}
                  onChange={(e) => { setUploadChannel(e.target.value); refreshMeta(e.target.value) }}
                  className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80"
                >
                  {(launcherChannels.length ? launcherChannels : [{ id: 'live' }]).map((c) => (
                    <option key={c.id} value={c.id}>{c.id}</option>
                  ))}
                </select>

                <input
                  type="file"
                  accept=".exe,application/octet-stream"
                  onChange={(e) => uploadInstaller(e.target.files?.[0])}
                  className="text-sm text-white/70"
                />
              </div>

              <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/70">
                <div className="font-semibold text-white/80">Current meta ({uploadChannel})</div>
                {launcherMeta ? (
                  <div className="mt-2 grid gap-1">
                    <div>File: <span className="text-white/80">{launcherMeta.filename}</span></div>
                    <div>Size: <span className="text-white/80">{launcherMeta.size_bytes ?? '—'}</span> bytes</div>
                    <div>SHA256: <span className="text-white/80 break-all">{launcherMeta.sha256 ?? '—'}</span></div>
                    <div>Protected: <span className="text-white/80">{String(launcherMeta.protected)}</span></div>
                  </div>
                ) : (
                  <div className="mt-2">No meta available.</div>
                )}
              </div>
            </Section>
          </div>
        )}

        {tab === 'beta' && (
          <div className="grid gap-6">
            <Section title="Generate Beta Keys" subtitle="Keys unlock a channel when redeemed in Account.">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-xs tracking-[0.28em] text-white/50">CHANNEL</div>
                  <input value={genChannel} onChange={(e) => setGenChannel(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80" placeholder="beta" />
                </div>
                <div>
                  <div className="text-xs tracking-[0.28em] text-white/50">COUNT</div>
                  <input type="number" value={genCount} onChange={(e) => setGenCount(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80" />
                </div>
                <div>
                  <div className="text-xs tracking-[0.28em] text-white/50">MAX USES</div>
                  <input type="number" value={genMaxUses} onChange={(e) => setGenMaxUses(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80" />
                </div>
                <div>
                  <div className="text-xs tracking-[0.28em] text-white/50">EXPIRES</div>
                  <input type="date" value={genExpiresAt} onChange={(e) => setGenExpiresAt(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80" />
                </div>
              </div>

              <div className="mt-4">
                <button onClick={generateKeys} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Generate</button>
              </div>

              {generated.length ? (
                <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="text-sm font-semibold text-white/80">Generated codes</div>
                  <pre className="mt-2 whitespace-pre-wrap text-xs text-white/70">{generated.join('\n')}</pre>
                  <div className="mt-2 text-xs text-white/45">Copy/paste these to your testers.</div>
                </div>
              ) : null}
            </Section>

            <Section title="Existing Keys" subtitle="Recent keys (limit 500).">
              <div className="overflow-auto rounded-xl border border-white/10">
                <table className="min-w-full text-sm">
                  <thead className="bg-black/30 text-white/60">
                    <tr>
                      <th className="text-left px-3 py-2">Code</th>
                      <th className="text-left px-3 py-2">Channel</th>
                      <th className="text-left px-3 py-2">Uses</th>
                      <th className="text-left px-3 py-2">Expires</th>
                      <th className="text-left px-3 py-2">Revoked</th>
                      <th className="text-left px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70">
                    {betaKeys.map((k) => (
                      <tr key={k.id} className="border-t border-white/10">
                        <td className="px-3 py-2 font-mono text-xs">{k.code}</td>
                        <td className="px-3 py-2">{k.channel}</td>
                        <td className="px-3 py-2">{k.uses}/{k.max_uses}</td>
                        <td className="px-3 py-2">{k.expires_at || '—'}</td>
                        <td className="px-3 py-2">{k.revoked ? 'yes' : 'no'}</td>
                        <td className="px-3 py-2">
                          {!k.revoked ? (
                            <button onClick={() => revokeKey(k.code)} className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs hover:bg-red-500/15">
                              Revoke
                            </button>
                          ) : (
                            <span className="text-xs text-white/40">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!betaKeys.length ? (
                      <tr><td className="px-3 py-3 text-white/50" colSpan={6}>No keys yet.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Section>
          </div>
        )}

        {tab === 'discord' && (
          <Section title="Discord Settings" subtitle="Role mapping for the Discord bot (verified role + channel roles)." actions={
            <button onClick={() => save('discord')} className="rounded-xl border border-white/15 bg-white/10 px-4 py-2 text-sm hover:bg-white/15">Save</button>
          }>
            <JsonEditor value={discordCfg} onChange={setDiscordCfg} />
            <div className="mt-3 text-xs text-white/45">
              The bot reads these IDs via the Bot API. Set BOT_API_KEY on both server and bot. Linking is handled from Account via OAuth.
            </div>
          </Section>
        )}

        {tab === 'admins' && (
          <Section title="Admin Allow-List" subtitle="Add/remove admin emails without redeploying.">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="flex-1 rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm outline-none focus:border-white/20"
                placeholder="admin@example.com"
              />
              <button onClick={addAdmin} className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15">Add</button>
            </div>

            <div className="mt-6 overflow-auto rounded-xl border border-white/10">
              <table className="min-w-full text-sm">
                <thead className="bg-black/30 text-white/60">
                  <tr>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Added</th>
                    <th className="text-left px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="text-white/70">
                  {admins.map((a) => (
                    <tr key={a.email} className="border-t border-white/10">
                      <td className="px-3 py-2">{a.email}</td>
                      <td className="px-3 py-2 text-xs">{a.created_at}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeAdmin(a.email)} className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1 text-xs hover:bg-red-500/15">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!admins.length ? <tr><td className="px-3 py-3 text-white/50" colSpan={3}>No admins yet.</td></tr> : null}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
